import { usePageStore } from '@/Components/templates/PageStore';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  type Environment,
  type PreloadedQuery,
  type UseQueryLoaderLoadQueryOptions,
  useRelayEnvironment,
  usePaginationFragment as useRelayPaginationFragment,
  useQueryLoader as useRelayQueryLoader,
} from 'react-relay';
import type { KeyType } from 'react-relay/relay-hooks/helpers';
import type { Options, RefetchFnDynamic } from 'react-relay/relay-hooks/useRefetchableFragmentNode';
import {
  type CacheConfig,
  type FetchQueryFetchPolicy,
  type GraphQLTaggedNode,
  type OperationType,
  type PayloadError,
  type Subscription,
  type VariablesOf,
  fetchQuery,
  getFragment,
  getPaginationMetadata,
  isFragment,
} from 'relay-runtime';
import type { PickKeys } from 'ts-essentials';
import { RelayEnvironment } from './relay-env';
import { type RelayStore, type RelayStoreKey, useRelayStore } from './useRelayStore';

/**
 * usePaginationFragment
 * This is a custom hook that extends relay's usePaginationFragment hook.
 * We use this instead of the traditional in order to retain the __id of the parent fragment.
 * We also create a custom refetch function that allows us to refetch without suspending the component.
 */

type UsePaginationFragmentFnReturn<
  TQuery extends OperationType,
  TKey extends KeyType,
  VarsFromQuery = Parameters<RefetchFnDynamic<TQuery, TKey>>[0],
  VarsFromStore = RelayStore[PickKeys<RelayStore, { __paginationQueryKeyType: TKey }>]['__paginationVariables'],
> = Omit<ReturnType<typeof useRelayPaginationFragment<TQuery, TKey>>, 'refetch'> & {
  isRefetching: boolean;
  refetch: (
    // if the store key doesn't exist then use the variables from the query
    vars: PickKeys<RelayStore, { __paginationQueryKeyType: TKey }> extends never ? VarsFromQuery : VarsFromStore,
    options?: Options
  ) => ReturnType<RefetchFnDynamic<TQuery, TKey>>;
  classicRefetch: (
    vars:
      | (PickKeys<RelayStore, { __paginationQueryKeyType: TKey }> extends never ? VarsFromQuery : VarsFromStore)
      | {
          [key: string]: unknown;
        },
    options?: Options
  ) => ReturnType<RefetchFnDynamic<TQuery, TKey>>;
};

type UsePaginationFragmentFnOpts = {
  save__id?: boolean;
  save__data?: boolean;
  environment?: Environment;
  usePageLoading?: boolean; // default true
};

export type UsePaginationFragmentFn = <TQuery extends OperationType, TKey extends KeyType>(
  fragmentInput: GraphQLTaggedNode,
  parentFragmentRef: TKey,
  opts?: UsePaginationFragmentFnOpts
) => UsePaginationFragmentFnReturn<TQuery, TKey>;

export const usePaginationFragment: UsePaginationFragmentFn = <TQuery extends OperationType, TKey extends KeyType>(
  fragmentInput: GraphQLTaggedNode,
  parentFragmentRef: TKey,
  opts?: UsePaginationFragmentFnOpts
): UsePaginationFragmentFnReturn<TQuery, TKey> => {
  const [isRefetching, setIsRefetching] = useState(false);
  const isRefetchingRef = useRef(false);

  const setRefetching = useCallback(
    (value: boolean) => {
      isRefetchingRef.current = value;
      setIsRefetching(value);
    },
    [setIsRefetching, isRefetchingRef]
  );

  const set__id = useRelayStore((state) => state.set__id);
  const setPaginationData = useRelayStore((state) => state.setPaginationData);

  const paginationData = useRelayPaginationFragment<TQuery, TKey>(fragmentInput, parentFragmentRef);

  const fragment = useMemo(() => (isFragment(fragmentInput) ? getFragment(fragmentInput) : null), [fragmentInput]);

  const __id: string | null = useMemo(() => {
    // pagination data should only have one property but we do not know the name of the property
    const key = Object.keys(paginationData?.data ?? {})?.[0];
    // @ts-ignore
    return paginationData?.data?.[key]?.__id ?? null;
  }, [paginationData?.data, fragment]);

  useEffect(() => {
    if (opts?.save__id === false) return;
    if (!fragment?.name || !__id) return;
    set__id(fragment.name as RelayStoreKey, __id);
  }, [__id]);

  useEffect(() => {
    if (opts?.save__data !== true) return;
    if (!fragment?.name || !paginationData?.data) return;

    // @ts-ignore
    setPaginationData(fragment.name, paginationData);
  }, [paginationData]);

  // page loading effect
  useEffect(() => {
    if (opts?.usePageLoading === false) return;

    const storePageLoading = usePageStore.getState().pageLoading;
    const isQueryLoading =
      paginationData?.isLoadingNext || isRefetchingRef.current || paginationData?.isLoadingPrevious;

    if (isQueryLoading === true && storePageLoading === false) {
      usePageStore.setState({ pageLoading: true });
    } else if (isQueryLoading === false && storePageLoading === true) {
      usePageStore.setState({ pageLoading: false });
    }
  }, [paginationData?.isLoadingNext, isRefetching, paginationData?.isLoadingPrevious, opts?.usePageLoading]);

  const refetchSubscriptionRef = useRef<Subscription | null>(null);

  useEffect(() => {
    return () => {
      if (refetchSubscriptionRef.current !== null) {
        refetchSubscriptionRef.current.unsubscribe();
        refetchSubscriptionRef.current = null;
      }
      if (opts?.usePageLoading !== false) {
        usePageStore.setState({ pageLoading: false });
      }
    };
  }, []);

  const refetch: RefetchFnDynamic<TQuery, TKey> = useCallback(
    (vars, options) => {
      if (isRefetchingRef.current === true) {
        return {
          dispose() {
            setRefetching(false);
          },
          closed: true,
          unsubscribe() {},
        };
      }

      if (refetchSubscriptionRef.current !== null) {
        refetchSubscriptionRef.current.unsubscribe();
        refetchSubscriptionRef.current = null;
      }

      setRefetching(true);

      const componentDisplayName = 'usePaginationFragment()';
      const fragmentNode = getFragment(fragmentInput);
      const { paginationRequest } = getPaginationMetadata(fragmentNode, componentDisplayName);

      const environment = opts?.environment ?? RelayEnvironment;

      const ret = fetchQuery(environment, paginationRequest, vars, {
        fetchPolicy: 'store-or-network',
      }).subscribe({
        complete: () => {
          setRefetching(false);
          paginationData?.refetch(vars, { ...options, fetchPolicy: 'store-only' });
        },
        error: (error: Error) => {
          setRefetching(false);
        },
        next: () => {
          setRefetching(false);
        },
        unsubscribe(subscription) {
          setRefetching(false);
          refetchSubscriptionRef.current = null;
          subscription.unsubscribe();
        },
      });

      refetchSubscriptionRef.current = ret;

      return {
        closed: ret.closed,
        dispose: () => {
          isRefetchingRef.current = false;
          setIsRefetching(false);
        },
        unsubscribe: ret.unsubscribe,
      };
    },
    [paginationData?.refetch, isRefetching, fragmentInput, setIsRefetching, opts?.environment]
  );

  return { ...paginationData, isRefetching, refetch, classicRefetch: paginationData?.refetch };
};

export type UseQueryLoaderFn = <TQuery extends OperationType>(
  query: GraphQLTaggedNode,
  initVariables: VariablesOf<TQuery>,
  options?: UseQueryLoaderLoadQueryOptions
) => {
  queryRef: PreloadedQuery<TQuery, Record<string, unknown>> | null | undefined;
  refetch: (variables: VariablesOf<TQuery>, options?: UseQueryLoaderLoadQueryOptions) => void;
  isRefetching: boolean;
};

export const useQueryLoader: UseQueryLoaderFn = <TQuery extends OperationType>(
  query: GraphQLTaggedNode,
  initVariables: VariablesOf<TQuery>,
  options?: UseQueryLoaderLoadQueryOptions
) => {
  const environment = useRelayEnvironment();
  const [queryRef, loadQuery] = useRelayQueryLoader<TQuery>(query);

  const [isRefetching, setIsRefetching] = useState<boolean>(false);

  useEffect(() => {
    if (!environment) return;
    loadQuery(initVariables, { fetchPolicy: 'store-or-network', ...options });
  }, [environment]);

  const refetch = useCallback(
    (variables: VariablesOf<TQuery>, options?: UseQueryLoaderLoadQueryOptions) => {
      if (!environment) return;
      if (isRefetching) return;
      setIsRefetching(true);

      // fetchQuery will fetch the query and write
      // the data to the Relay store. This will ensure
      // that when we re-render, the data is already
      // cached and we don't suspend
      fetchQuery<TQuery>(environment, query as GraphQLTaggedNode, variables, {
        fetchPolicy: 'store-or-network',
      }).subscribe({
        complete: () => {
          setIsRefetching(false);

          // *After* the query has been fetched, we call
          // loadQuery again to re-render with a new
          // queryRef.
          // At this point the data for the query should
          // be cached, so we use the 'store-only'
          // fetchPolicy to avoid suspending.
          loadQuery(variables, { ...options, fetchPolicy: 'store-only' });
        },
        error: () => {
          setIsRefetching(false);
        },
      });
    },
    [isRefetching, setIsRefetching, environment, query, loadQuery]
  );

  return { queryRef, refetch, isRefetching };
};

export const useRelayFetchQuery = <T extends OperationType>(query: GraphQLTaggedNode) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<T['response'] | null>(null);
  const [errors, setErrors] = useState<PayloadError[] | null>(null);

  const refetch = useCallback(
    (
      variables?: T['variables'],
      config?: {
        onComplete?: (data: T['response'], errors?: PayloadError[]) => void;
        environment?: Environment;
        cacheConfig?: {
          networkCacheConfig?: CacheConfig | null | undefined;
          fetchPolicy?: FetchQueryFetchPolicy | null | undefined;
        } | null;
      }
    ) => {
      const input = variables as T['variables'];
      let newData: T['response'];
      let error: PayloadError;

      const cacheConfig: NonNullable<typeof config>['cacheConfig'] = {
        ...config?.cacheConfig,
        fetchPolicy: config?.cacheConfig?.fetchPolicy ?? 'store-or-network',
      };

      fetchQuery(config?.environment ?? RelayEnvironment, query, input, cacheConfig).subscribe({
        start: () => {
          setLoading(true);
        },
        next: (data) => {
          newData = data;
          setLoading(false);
        },
        error: (_error: PayloadError, isUncaughtThrownError?: boolean) => {
          error = _error;
          setLoading(false);
        },
        complete: () => {
          setLoading(false);
          config?.onComplete?.(newData, !!error ? [error] : undefined);

          setData(newData);
          setErrors(!!error ? [error] : null);
        },
      });
    },
    [query, setLoading, setData, setErrors]
  );

  return [refetch, loading, data, errors] as const;
};
